import React, { useEffect } from 'react';
import { Box, Typography, Button, ButtonGroup } from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
    LineChart,
    Line,
    XAxis,
    YAxis,
    Tooltip,
    CartesianGrid,
    ResponsiveContainer
} from 'recharts';
import Axios from 'axios';
import { useImmerReducer } from 'use-immer';

const periods = ['day', 'week', 'month', 'year'];

function VisitsPage() {
    const { t } = useTranslation();
    const { config, environment } = require('../../index');

    const initialState = {
        period: 'week',
        chartData: [],
        totalVisits: 0,
        visitsToday: 0,
        loading: true,
        xKey: 'day'
    };

    function reducer(draft, action) {
        switch (action.type) {
            case 'setPeriod':
                draft.period = action.period;
                break;
            case 'setData':
                draft.chartData = action.chartData;
                draft.totalVisits = action.totalVisits;
                draft.visitsToday = action.visitsToday;
                draft.xKey = action.xKey;
                draft.loading = false;
                break;
            case 'setLoading':
                draft.loading = action.loading;
                break;
            default:
                break;
        }
    }

    const [state, dispatch] = useImmerReducer(reducer, initialState);

    const formatLabel = (value, key) => {
        if (!value) return value;

        if (key === 'hour') {
            return `${value}:00`;
        }

        if (key === 'month' && value.length === 6) {
            const year = value.substring(0, 4);
            const month = value.substring(4, 6);
            return `${year}.${month}`;
        }

        if (key === 'day' && value.length === 8) {
            const year = value.substring(0, 4);
            const month = value.substring(4, 6);
            const day = value.substring(6, 8);
            return `${year}.${month}.${day}`;
        }

        return value;
    };

    const fetchVisits = async (selectedPeriod) => {
        try {
            dispatch({ type: 'setLoading', loading: true });

            const res = await Axios.get(
                `${config[environment].visitsEndpoint}?period=${selectedPeriod}`
            );

            const data = res.data.data || [];


            let xKey = 'day';
            if (data.length > 0) {
                if ('hour' in data[0]) xKey = 'hour';
                else if ('month' in data[0]) xKey = 'month';
                else xKey = 'day';
            }

            dispatch({
                type: 'setData',
                chartData: data,
                totalVisits: res.data.total_visits || 0,
                visitsToday: res.data.visits_today || 0,
                xKey
            });
        } catch (err) {
            console.error(err);
            dispatch({ type: 'setLoading', loading: false });
        }
    };

    useEffect(() => {
        fetchVisits(state.period);
    }, [state.period]);

    return (
        <Box sx={{ p: 3 }}>
            <Typography variant="h4" sx={{ mb: 2 }}>
                {t('Footer.VisitsTitle')}
            </Typography>

            <ButtonGroup sx={{ mb: 3 }}>
                {periods.map((p) => (
                    <Button
                        key={p}
                        variant={p === state.period ? 'contained' : 'outlined'}
                        onClick={() =>
                            dispatch({ type: 'setPeriod', period: p })
                        }
                    >
                        {p === 'day'
                            ? t('Visits.Daily', 'Napi')
                            : p === 'week'
                                ? t('Visits.Weekly', 'Heti')
                                : p === 'month'
                                    ? t('Visits.Monthly', 'Havi')
                                    : t('Visits.Yearly', 'Éves')}
                    </Button>
                ))}
            </ButtonGroup>

            {state.loading ? (
                <Typography>{t('Common.Loading', 'Betöltés...')}</Typography>
            ) : (
                <>
                    <Typography>
                        {t('Footer.TotalVisits')}: {state.totalVisits}
                    </Typography>

                    <Typography>
                        {t('Visits.CurrentPeriodLastValue', 'Aktuális időszak utolsó értéke')}: {state.visitsToday}
                    </Typography>

                    <Box sx={{ mt: 4, height: 400 }}>
                        <ResponsiveContainer width="100%" height="100%">
                            <LineChart data={state.chartData}>
                                <CartesianGrid stroke="#ccc" />

                                <XAxis
                                    dataKey={state.xKey}
                                    tickFormatter={(value) =>
                                        formatLabel(value, state.xKey)
                                    }
                                    interval="preserveStartEnd"
                                    minTickGap={20}
                                />

                                <YAxis />

                                <Tooltip
                                    labelFormatter={(label) =>
                                        formatLabel(label, state.xKey)
                                    }
                                    formatter={(value) => [
                                        value,
                                        t('Footer.Visits')
                                    ]}
                                />

                                <Line
                                    type="monotone"
                                    dataKey="visits"
                                    stroke="#8884d8"
                                />
                            </LineChart>
                        </ResponsiveContainer>
                    </Box>
                </>
            )}
        </Box>
    );
}

export default VisitsPage;
